import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';

import { ReactComponent as Calender } from '../../assets/icons/clock-icon.svg';
// import { ReactComponent as LeftArrow } from '../../assets/icons/left-arrow.svg';
import {
  convertTo12HourFormat,
  convertTo24Hour,
  getPeriodCurrentTime,
  parseTime,
} from '../../shared-functions/DataTime';
import { time12HoursValidation } from '../../shared-functions/Validation';
import MyButton from '../button/Button';

interface ScrollColumnProperties {
  options: (string | number)[];
  value: string | number;
  onChange: (value: string | number) => void;
  label: string;
  padZero?: boolean;
}

/**
 * Scrollable column component for time picker
 * @param {ScrollColumnProperties} param0 - The props
 * @returns {any} component
 */
function ScrollColumn({ options, value, onChange, label, padZero }: ScrollColumnProperties) {
  const containerReference = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerReference.current) {
      const index = options.indexOf(value);
      if (index !== -1) {
        containerReference.current.scrollTop = index * 32;
      }
    }
  }, [value, options]);

  /**
   * Handle the scroll event
   * @param {any} event - scroll event
   */
  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const element = event.currentTarget;
    const index = Math.round(element.scrollTop / 32);
    if (options[index] !== undefined && options[index] !== value) {
      onChange(options[index]);
    }
  };

  return (
    <div className="scroll-column">
      <div className="column-label">{label}</div>
      <div className="scroll-area" ref={containerReference} onScroll={handleScroll}>
        <div className="spacer" />
        {options.map((option) => (
          <div
            key={option}
            className={`scroll-item ${option === value ? 'selected' : ''}`}
            onClick={() => onChange(option)}
            role="presentation">
            {padZero && typeof option === 'number' ? String(option).padStart(2, '0') : option}
          </div>
        ))}
        <div className="spacer" />
      </div>
    </div>
  );
}

ScrollColumn.defaultProps = {
  padZero: false,
};

interface CustomDateRangeProperties {
  inputLabel?: string;
  value: string;
  name: string;
  placeholder?: string;
  onChangeValue: (name: string, value: string) => void;
  valueRequired?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  errorHandle?: boolean;
  errorHandlingFunction?: (value: boolean) => void;
  innerLabel?: string;
  minTime?: string; // HH:mm in 24-hour format — restricts past times (used when today is selected)
  error?: boolean;
  errorMessage?: string;
}

/**
 * Custom the Date Picker function
 * @param {any} param0 - The param0
 * @param {any} param0.inputLabel - The inputLabel prop
 * @param {any} param0.name - The name prop
 * @param {any} param0.value - The value prop
 * @param {any} param0.placeholder - The placeholder prop
 * @param {any} param0.onChangeValue - The onChangeValue prop
 * @param {any} param0.valueRequired - The valueRequired prop
 * @param {any} param0.errorHandlingFunction - The errorHandlingFunction prop
 * @param {any} param0.readOnly - The readOnly prop
 * @param {any} param0.disabled - The disabled prop
 * @param {any} param0.errorHandle - The errorHandle prop
 * @param {any} param0.innerLabel - The innerLabel prop
 * @param {any} param0.minTime - The minTime prop
 * @param {any} param0.error - The error prop
 * @param {any} param0.errorMessage - The errorMessage prop
 * @returns {any} component
 */
function CustomTimePicker({
  inputLabel,
  name,
  value,
  placeholder,
  onChangeValue,
  valueRequired,
  errorHandlingFunction = () => {},
  readOnly,
  disabled,
  errorHandle,
  innerLabel,
  minTime,
  error,
  errorMessage,
}: CustomDateRangeProperties) {
  const format: string = '12 hours';
  const { t } = useTranslation('maintenanceSchedule');
  const reference = useRef<HTMLDivElement>(null);
  // const wheelTimeout = useRef<any>(null);
  const [open, setOpen] = useState<boolean>(false);

  const [timeValue, setTime] = useState<{
    hours: number;
    minutes: number;
    noon: string;
  }>({
    hours: 0,
    minutes: 0,
    noon: 'AM',
  });

  const [style, setStyle] = useState<any>({});
  const [validationError, setValidationError] = useState<string>('');

  const [hours, setHours] = useState<number>(
    format === '12 hours' ? new Date().getHours() % 12 || 12 : new Date().getHours(),
  );
  const [minutes, setMinutes] = useState<number>(new Date().getMinutes());
  const [noon, setNoon] = useState<string>(getPeriodCurrentTime());
  /**
   * Handle the Value change
   */
  const handleValueChange = () => {
    const selected24 = convertTo24Hour(`${hours}:${minutes} ${noon}`);
    if (minTime) {
      // minTime is in HH:mm format; compare numerically
      const [minH, minM] = minTime.split(':').map(Number);
      const [selH, selM] = selected24.split(':').map(Number);
      if (selH < minH || (selH === minH && selM < minM)) {
        setValidationError(t('timeMustBeInFuture'));
        return;
      }
    }
    setValidationError('');
    onChangeValue(name, selected24);
    setTime({
      hours,
      minutes,
      noon,
    });
    setOpen(false);
    errorHandlingFunction(true);
  };
  /**
   * Handle the Clear value
   */
  const handleValueClear = () => {
    onChangeValue(name, '');
    setTime({
      hours: 0,
      minutes: 0,
      noon: 'PM',
    });
  };

  /**
   * Handle the time change
   * @param {any} time time string
   * @param {any} status status string
   */
  // const changeTime = (time: string, status: string) => {
  //   if (time === 'hours') {
  //     const changeValue = status === 'add' ? hours + 1 : hours - 1;
  //     if (
  //       (format === '12 hours' && changeValue > 0 && changeValue < 13) ||
  //       (format === '24 hours' && changeValue >= 0 && changeValue < 23)
  //     ) {
  //       setHours(changeValue);
  //     }
  //   } else if (time === 'minute') {
  //     const changeValue = status === 'add' ? minutes + 1 : minutes - 1;
  //     if (changeValue >= 0 && changeValue < 60) {
  //       setMinutes(changeValue);
  //     }
  //   } else if (time === 'noon') {
  //     setNoon(noon === 'AM' ? 'PM' : 'AM');
  //   }
  // };

  /**
   * Handle mouse wheel scroll to change time
   * @param {any} event - The wheel event
   * @param {string} type - 'hours' | 'minute' | 'noon'
   */
  // const handleWheel = (event: React.WheelEvent, type: string) => {
  //   if (wheelTimeout.current) return;

  //   // Set a timeout to throttle the wheel event
  //   wheelTimeout.current = setTimeout(() => {
  //     wheelTimeout.current = null;
  //   }, 200);

  //   if (event.deltaY > 0) {
  //     changeTime(type, 'add');
  //   } else if (event.deltaY < 0) {
  //     changeTime(type, 'sub');
  //   }
  // };

  /**
   * Handle the Input click
   * @param {any} event event
   */
  const openCalenderView = (event: any) => {
    if (!disabled && !readOnly) {
      setOpen(true);
      let rect;
      if (event.target.className === 'custom-input-container') {
        rect = event.target.getBoundingClientRect();
      } else {
        rect = event.target.closest('.custom-input-container').getBoundingClientRect();
      }
      let calculatedLeft = rect.left;
      const estimatedPopupWidth = 220; // Approximate width of time picker popup
      if (calculatedLeft + estimatedPopupWidth > window.innerWidth) {
        calculatedLeft = window.innerWidth - estimatedPopupWidth - 20; // Keep 20px padding from right edge
      }

      if (window.innerHeight > rect.top + rect.height + 215) {
        setStyle({
          top: rect.top + rect.height + 4,
          left: calculatedLeft,
        });
      } else {
        setStyle({
          top: rect.top - 215,
          left: calculatedLeft,
        });
      }
    }
  };

  /**
   * Handle the Input click
   * @param {any} event event
   */
  const handleChange = (event: any) => {
    let input = event.target.value.toUpperCase(); // Normalize input to uppercase for AM/PM
    const currentName = event.target.name;
    if (currentName === 'hours' && Number(input) >= 13) {
      input = '12';
    } else if (currentName === 'minutes' && Number(input) >= 60) {
      input = '59';
    } else if (currentName === 'noon' && !['AM', 'PM', 'A', 'P', ''].includes(input)) {
      input = 'AM';
    }

    setTime((previous) => ({
      ...previous,
      [currentName]: input,
    }));
  };

  /**
   * Handle close the calender over flow click function
   * @param {any} event mouse event
   */
  const handleClickOutside = (event: MouseEvent) => {
    if (reference.current && !reference.current.contains(event.target as Node)) {
      setOpen(false);
    }
  };
  useEffect((): void => {
    document.addEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (Object.keys(timeValue).length > 0) {
      if (
        time12HoursValidation(
          `${String(timeValue.hours).replace(/^0+/, '').padStart(2, '0')}:${String(timeValue.minutes).replace(/^0+/, '').padStart(2, '0')} ${timeValue.noon}`,
        )
      ) {
        errorHandlingFunction(true);
        onChangeValue(
          name,
          convertTo24Hour(
            `${String(timeValue.hours).replace(/^0+/, '').padStart(2, '0')}:${String(timeValue.minutes).replace(/^0+/, '').padStart(2, '0')} ${timeValue.noon}`,
          ),
        );
        setValidationError('');
      } else {
        errorHandlingFunction(false);
        onChangeValue(name, '');
        setValidationError(t('timeFormatIsWrong'));
      }
    }
  }, [timeValue]);

  useEffect(() => {
    if (value) {
      const oldValue = value
        ? parseTime(value)
        : {
            hours: 0,
            minutes: 0,
            noon: 'AM',
          };

      setTime(oldValue);
    }
  }, [value]);
  return (
    <div className="custom-input-time-wrap">
      {inputLabel && (
        <p className={`input-label ${error ? 'error' : ''}`}>
          {inputLabel}
          {valueRequired && <span>*</span>}
        </p>
      )}
      <div className="custom-time-input">
        <div
          className={`custom-input-container ${disabled ? 'disabled' : ''} ${error ? 'error' : ''}`}
          onClick={openCalenderView}
          role="presentation">
          {innerLabel ? (
            <div className={`floating-time-view ${open || value ? 'active' : ''}`}>
              <span className="floating-label">{innerLabel}</span>

              <div className="time-value">
                {value ? convertTo12HourFormat(value) : placeholder || '--:--'}
              </div>
            </div>
          ) : (
            <span>
              <input
                id="timeInput"
                type="number"
                autoComplete="off"
                name="hours"
                value={
                  timeValue.hours
                    ? String(timeValue.hours).replace(/^0+/, '').padStart(2, '0')
                    : '00'
                }
                onChange={handleChange}
                placeholder={placeholder}
                onKeyUp={() => setOpen(false)}
                min={1}
                max={12}
              />
              :
              <input
                id="timeInput"
                type="number"
                autoComplete="off"
                name="minutes"
                value={
                  timeValue.minutes
                    ? String(timeValue.minutes).replace(/^0+/, '').padStart(2, '0')
                    : '00'
                }
                onChange={handleChange}
                placeholder={placeholder}
                onKeyUp={() => setOpen(false)}
              />
              <input
                id="timeInput"
                type="text"
                autoComplete="off"
                name="noon"
                value={timeValue.noon}
                onChange={handleChange}
                placeholder={placeholder}
                onKeyUp={() => setOpen(false)}
              />
            </span>
          )}
          <span className="icon">
            <Calender />
          </span>
        </div>
        {open &&
          ReactDOM.createPortal(
            <div className="timer-box" ref={reference} style={style}>
              <div className="time-picker">
                <div className="time-picker-body scroll-style">
                  <ScrollColumn
                    label="HH"
                    options={
                      format === '12 hours'
                        ? Array.from({ length: 12 }, (_, index) => index + 1)
                        : Array.from({ length: 24 }, (_, index) => index)
                    }
                    value={hours}
                    onChange={(selectedValue) => setHours(selectedValue as number)}
                    padZero
                  />
                  <ScrollColumn
                    label="MM"
                    options={Array.from({ length: 60 }, (_, index) => index)}
                    value={minutes}
                    onChange={(selectedValue) => setMinutes(selectedValue as number)}
                    padZero
                  />
                  {format === '12 hours' && (
                    <ScrollColumn
                      label="AM/PM"
                      options={['AM', 'PM']}
                      value={noon}
                      onChange={(selectedValue) => setNoon(selectedValue as string)}
                    />
                  )}
                </div>
                <div className="time-picker-footer">
                  <div className="footer-button-left">
                    <div
                      className="close-button text-danger"
                      onClick={handleValueClear}
                      role="presentation">
                      Clear
                    </div>
                  </div>
                  <div className="footer-button-right">
                    <MyButton label="Ok" buttonType="submit" onClickFunc={handleValueChange} />
                  </div>
                </div>
              </div>
            </div>,
            document.body,
          )}
      </div>
      {(error || (errorHandle && validationError)) && (
        <p className="text-danger">{errorMessage || validationError}</p>
      )}
    </div>
  );
}

CustomTimePicker.defaultProps = {
  inputLabel: '',
  placeholder: '',
  /**
   * common function
   */
  errorHandlingFunction: () => {},
  valueRequired: false,
  readOnly: false,
  disabled: false,
  errorHandle: false,
  innerLabel: '',
  minTime: undefined,
  error: false,
  errorMessage: '',
};

export default CustomTimePicker;
